import { message } from "antd";

// Date conversion with fullDate
export const formatDate = (selectedDate) => {
  if (!selectedDate) return "N/A";

  try {
    let date;

    // Handle string dates in "DD-MM-YYYY" format
    if (typeof selectedDate === "string") {
      // Check if it matches DD-MM-YYYY pattern
      if (selectedDate.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const [day, month, year] = selectedDate.split("-").map(Number);
        date = new Date(year, month - 1, day);
      } else {
        // Try parsing as ISO string or other date format
        date = new Date(selectedDate);
      }
    } else {
      // Handle Date objects or timestamps
      date = new Date(selectedDate);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date:", selectedDate);
      return "Invalid date";
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch (error) {
    console.error("Date formatting error:", error, "Input:", selectedDate);
    return "Invalid date";
  }
};

// Date conversion with timestamp
export const formatDateWithTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(parseInt(timestamp, 10));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

// Converting UTC time to IST
export const UTCtoISTConversion = (utcDate) => {
  const date = new Date(utcDate);
  const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  return istDate;
};

export const formatToMMDDYYYY = (isoDateStr) => {
  if (!isoDateStr) return "";
  const date = new Date(isoDateStr);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

// Pixel or feet conversion

export const formatBillboardSize = (value) => {
  if (!value) return null;
  return value
    .replace(/\s*feet/gi, "'") // Matches "feet", " Feet", "  feet", etc.
    .replace(/\s*inches/gi, '"') // Matches "inches" with optional space
    .replace(/\s*pixels/gi, " px"); // Matches "pixels" with optional space
};

// Truncating long numbers
export const truncatedNumber = (numberToFormat) => {
  if (numberToFormat == null) return "";
  return numberToFormat.length > 7
    ? numberToFormat.slice(0, 6) + "..."
    : numberToFormat;
};

// For notification messages truncating in navbar
export const truncateDescriptionText = (text) => {
  if (text == null) return "";
  return text.length > 40 ? text.slice(0, 40) + "..." : text;
};

export const formatToUSCurrency = (amount) => {
  if (isNaN(amount)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

// Getting terms type based on api response
export const formatTermType = (termType) => {
  if (!termType) return "";
  switch (termType) {
    case "stripe_percent":
      return "Upfront Amount";
    case "net_15_percent":
      return "Net 15";
    case "net_30_percent":
      return "Net 30";
    case "net_45_percent":
      return "Net 45";
    case "net_60_percent":
      return "Net 60";
    case "net_90_percent":
      return "Net 90";
  }
};

//copy text
export const handleCopy = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text || "");
      message.success("Title copied to clipboard!");
    } else {
      // fallback for non-https or unsupported browsers
      const textarea = document.createElement("textarea");
      textarea.value = text || "";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);

      message.success("Title copied to clipboard!");
    }
  } catch (err) {
    message.error("Failed to copy!");
  }
};

// Restricts numeric and exponential key inputs. Allows only alphabetic characters on keydown.//

export const restrictNumbersAndExponential = (e) => {
  if ((e.key >= "0" && e.key <= "9") || ["+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

// Cleans input value to allow only alphabets (A–Z, a–z).//

export const allowOnlyAlphabets = (e) => {
  e.target.value = e.target.value.replace(/[^A-Za-z]/g, "");
};
export const blockPasteInvalid = (e) => {
  const pasteData = e.clipboardData.getData("text");
  if (!/^[A-Za-z]+$/.test(pasteData)) {
    e.preventDefault();
  }
};
export const allowAlphabetsWithInnerSpace = (e) => {
  let value = e.target.value;

  // 1. Remove anything that's not alphabet or space
  value = value.replace(/[^A-Za-z ]/g, "");

  // 2. Prevent leading spaces
  value = value.replace(/^\s+/g, "");

  // 3. Prevent trailing spaces
  value = value.replace(/\s+$/g, "");

  e.target.value = value;
};

export const capitalizeFirst = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

export const containsPersonalInfo = (text = "") => {
  const value = text.toLowerCase();
  /* ---------- EMAIL WITHOUT @ (gmail.com case) ---------- */
  const emailRegex =
    /\b[a-z0-9._%+-]{3,}(gmail|yahoo|outlook|hotmail|icloud|protonmail)\.(com|in|net|org)\b/;

  /* ---------- PHONE NUMBERS ---------- */
  const phoneRegex =
    /(\+?\d{1,3}[\s-]?)?(\(?\d{2,4}\)?[\s-]?)?\d{3,4}[\s-]?\d{4}\b/;

  /* ---------- OBFUSCATED EMAIL ---------- */
  const obfuscatedEmail = /\b(at|@)\b.*\b(dot|\.)\b/;

  /* ---------- LONG DIGIT SEQUENCE ---------- */
  const longDigitSequence = /\b\d{8,}\b/; // catches pasted numbers

  return (
    emailRegex.test(value) ||
    phoneRegex.test(value) ||
    obfuscatedEmail.test(value) ||
    longDigitSequence.test(value)
  );
};
export const formatMetric = (value) => {
  const num = Number(value);

  if (isNaN(num)) return value;

  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${num / 1_000}K`;

  return num?.toString();
};
/*
 * Formats product location for UI display.
 * Filters out empty or undefined fields and joins valid values with a comma and space.
 * Returns "N/A" when no location data is available.
*/
export const formatLocation = (product) => {
  const location = [
    product?.city,
    product?.state,
    product?.country_name,
    product?.zipcode
  ]
    .filter(Boolean)
    .join(", ")
  return location || "N/A";
};
